import React, { useState, useEffect } from 'react';
import { Form, Input, DatePicker } from 'antd';
import { ButtonCommon } from 'components/common';
import { Funnel, XCircle } from '@phosphor-icons/react';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import { EInvoiceFilter } from 'types/invoice.type';

dayjs.extend(quarterOfYear);

export enum DatePreset {
  THIS_MONTH = 'THIS_MONTH',
  LAST_MONTH = 'LAST_MONTH',
  Q1 = 'Q1',
  Q2 = 'Q2',
  Q3 = 'Q3',
  Q4 = 'Q4',
  THIS_YEAR = 'THIS_YEAR',
  LAST_YEAR = 'LAST_YEAR',
  CUSTOM = 'CUSTOM',
}

const PRESETS = [
  { label: 'Tháng này', value: DatePreset.THIS_MONTH },
  { label: 'Tháng trước', value: DatePreset.LAST_MONTH },
  { label: 'Quý 1 năm nay', value: DatePreset.Q1 },
  { label: 'Quý 2 năm nay', value: DatePreset.Q2 },
  { label: 'Quý 3 năm nay', value: DatePreset.Q3 },
  { label: 'Quý 4 năm nay', value: DatePreset.Q4 },
  { label: 'Năm nay', value: DatePreset.THIS_YEAR },
  { label: 'Năm trước', value: DatePreset.LAST_YEAR },
  { label: 'Lựa chọn khác', value: DatePreset.CUSTOM },
];

const PRESET_NOTE_LABELS: Record<DatePreset, string> = {
  [DatePreset.THIS_MONTH]: 'Tháng này',
  [DatePreset.LAST_MONTH]: 'Tháng trước',
  [DatePreset.Q1]: 'Quý 1',
  [DatePreset.Q2]: 'Quý 2',
  [DatePreset.Q3]: 'Quý 3',
  [DatePreset.Q4]: 'Quý 4',
  [DatePreset.THIS_YEAR]: 'Năm nay',
  [DatePreset.LAST_YEAR]: 'Năm trước',
  [DatePreset.CUSTOM]: 'Lựa chọn khác',
};

const getRangeFromPreset = (preset: DatePreset): [Dayjs, Dayjs] | null => {
  const now = dayjs();
  switch (preset) {
    case DatePreset.THIS_MONTH:
      return [now.startOf('month'), now.endOf('month')];

    case DatePreset.LAST_MONTH: {
      const lastMonth = now.subtract(1, 'month');
      return [lastMonth.startOf('month'), lastMonth.endOf('month')];
    }

    case DatePreset.Q1:
      return [now.quarter(1).startOf('quarter'), now.quarter(1).endOf('quarter')];
    case DatePreset.Q2:
      return [now.quarter(2).startOf('quarter'), now.quarter(2).endOf('quarter')];
    case DatePreset.Q3:
      return [now.quarter(3).startOf('quarter'), now.quarter(3).endOf('quarter')];
    case DatePreset.Q4:
      return [now.quarter(4).startOf('quarter'), now.quarter(4).endOf('quarter')];

    case DatePreset.THIS_YEAR:
      return [now.startOf('year'), now.endOf('year')];

    case DatePreset.LAST_YEAR: {
      const lastYear = now.subtract(1, 'year');
      return [lastYear.startOf('year'), lastYear.endOf('year')];
    }

    default:
      return null;
  }
};

const { RangePicker } = DatePicker;

const getDateRangeNote = (value?: [Dayjs | null, Dayjs | null] | null) => {
  if (!value?.[0] || !value?.[1]) return null;

  const [start, end] = value;

  for (const preset of PRESETS) {
    if (preset.value === DatePreset.CUSTOM) continue;

    const range = getRangeFromPreset(preset.value);
    if (!range) continue;

    if (start.isSame(range[0], 'day') && end.isSame(range[1], 'day')) {
      return `(${PRESET_NOTE_LABELS[preset.value]})`;
    }
  }

  return `(${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')})`;
};

interface AccountingDateRangeProps {
  value?: [Dayjs, Dayjs] | null;
  onChange?: (dates: [Dayjs, Dayjs] | null) => void;
}

const AccountingDateRange: React.FC<AccountingDateRangeProps> = ({ value, onChange }) => {
  const [pickerValue, setPickerValue] = useState<Dayjs>(dayjs());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (value && value[0]) {
      setPickerValue(value[0]);
    }
  }, [value]);

  const handlePresetClick = (presetValue: DatePreset) => {
    if (presetValue === DatePreset.CUSTOM) {
      setOpen(true);
      return;
    }

    const range = getRangeFromPreset(presetValue);
    if (range && onChange) {
      onChange(range);
      setPickerValue(range[0]);
      setOpen(false);
    }
  };

  return (
    <RangePicker
      open={open}
      value={value}
      pickerValue={pickerValue}
      onOpenChange={setOpen}
      onClick={() => setOpen(true)}
      onCalendarChange={(dates) => {
        if (dates?.[0]) setPickerValue(dates[0]);
      }}
      onChange={(dates) => {
        onChange?.(dates as [Dayjs, Dayjs]);
        if (dates?.[0]) setPickerValue(dates[0]);
      }}
      panelRender={(panel) => (
        <div className='flex'>
          <div className='w-[160px] border-r border-gray-100 p-2 space-y-1 bg-white'>
            {PRESETS.map((item) => (
              <div
                key={item.value}
                className='px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm rounded transition-colors text-gray-700 hover:text-blue-600'
                onMouseDown={(e) => {
                  e.preventDefault();
                  handlePresetClick(item.value);
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
          <div>{panel}</div>
        </div>
      )}
      className='w-full'
      size='large'
      format='DD/MM/YYYY'
    />
  );
};

interface FilterEInvoiceProps {
  initialFilter?: Partial<EInvoiceFilter>;
  onApplyFilter: (filter: EInvoiceFilter) => void;
  onClearFilter: () => void;
}

const FilterEInvoice: React.FC<FilterEInvoiceProps> = ({ initialFilter, onApplyFilter, onClearFilter }) => {
  const [form] = Form.useForm();
  const selectedDateRange = Form.useWatch('dateRange', form) as [Dayjs | null, Dayjs | null] | null | undefined;

  const defaultFilter = {
    ...initialFilter,
    listMode: initialFilter?.listMode ?? 'all',
    dateRange: initialFilter?.dateRange ?? [dayjs().startOf('quarter'), dayjs().endOf('quarter')],
  };
  const dateRangeNote = getDateRangeNote(selectedDateRange ?? defaultFilter.dateRange);

  useEffect(() => {
    if (initialFilter) {
      form.setFieldsValue(initialFilter);
    }
  }, [initialFilter, form]);

  const handleClearFilter = () => {
    form.resetFields();
    onClearFilter();
  };

  const handleApplyFilter = () => {
    form.validateFields().then((values) => {
      onApplyFilter(values);
    });
  };

  return (
    <div className='flex h-full min-h-0 flex-col bg-white'>
      <div className='flex-1 px-4 py-4'>
        <Form form={form} layout='vertical' initialValues={defaultFilter} className='filter-form filter-einvoice'>
          <div className='flex flex-col'>
            {/* <Form.Item
              className='mb-4'
              label={<span className='text-[13px] font-semibold text-gray-800'>Nhóm hoá đơn</span>}
              name='listMode'
            >
              <Select
                className='einvoice-group-select'
                placeholder='Chọn nhóm hoá đơn'
                suffixIcon={<CaretDown size={18} />}
                options={[
                  { value: 'all', label: 'Tất cả' },
                  { value: 'adjustments', label: 'Hóa đơn điều chỉnh' },
                  { value: 'replacements', label: 'Hóa đơn thay thế' },
                ]}
                size='large'
              />
            </Form.Item> */}

            {/* <Form.Item
              className='mb-4'
              label={<span className='text-[13px] font-semibold text-gray-800'>Loại hoá đơn</span>}
              name='invoiceType'
            >
              <Select
                className='einvoice-type-select'
                placeholder='Chọn loại hoá đơn'
                allowClear={{ clearIcon: <XCircle weight='fill' size={18} /> }}
                suffixIcon={<CaretDown size={18} />}
                options={[
                  { value: InvoiceType.DRAFT, label: 'Nháp' },
                  { value: InvoiceType.NEW, label: 'Hóa đơn mới' },
                  { value: InvoiceType.CANCELLED, label: 'Hóa đơn xóa bỏ' },
                  { value: InvoiceType.REPLACEMENT, label: 'Hóa đơn thay thế' },
                  { value: InvoiceType.REPLACED, label: 'Hóa đơn bị thay thế' },
                  { value: InvoiceType.ADJUST_INCREASE, label: 'Hóa đơn điều chỉnh tăng' },
                  { value: InvoiceType.ADJUST_DECREASE, label: 'Hóa đơn điều chỉnh giảm' },
                  { value: InvoiceType.ADJUST_INFO, label: 'Hóa đơn điều chỉnh thông tin' },
                  { value: InvoiceType.ADJUST_BOTH, label: 'Hóa đơn điều chỉnh tăng/giảm' },
                  { value: InvoiceType.ADJUSTED, label: 'Hóa đơn bị điều chỉnh' },
                ]}
                size='large'
              />
            </Form.Item> */}

            <Form.Item
              className='mb-4'
              label={<span className='text-[13px] font-semibold text-gray-800'>Mã tham chiếu</span>}
              name='MaTraCuu'
            >
              <Input
                placeholder='Nhập mã tham chiếu'
                allowClear={{ clearIcon: <XCircle weight='fill' size={18} /> }}
                size='large'
              />
            </Form.Item>

            <Form.Item
              className='mb-4'
              label={<span className='text-[13px] font-semibold text-gray-800'>Thông tin khách hàng</span>}
              name='TTKhachHang'
            >
              <Input
                placeholder='Nhập thông tin khách hàng'
                allowClear={{ clearIcon: <XCircle weight='fill' size={18} /> }}
                size='large'
              />
            </Form.Item>

            <Form.Item
              className='mb-4'
              label={
                <span className='flex flex-col'>
                  <span className='text-[13px] font-semibold text-gray-800'>Thời gian tạo hoá đơn điện tử</span>
                  {dateRangeNote && <span className='mt-0.5 text-[11px] italic text-gray-500'>{dateRangeNote}</span>}
                </span>
              }
              name='dateRange'
            >
              <AccountingDateRange />
            </Form.Item>

            {/* <Form.Item
              className='mb-0'
              label={<span className='text-[13px] font-semibold text-gray-800'>Trạng thái hoá đơn</span>}
              name='TThaiHDon'
            >
              <Select
                className='einvoice-status-select'
                placeholder='Chọn trạng thái hoá đơn'
                allowClear={{ clearIcon: <XCircle weight='fill' size={18} /> }}
                suffixIcon={<CaretDown size={18} />}
                options={[
                  { value: -1, label: 'Tất cả ' },
                  { value: 1, label: 'Nháp' },
                  { value: 2, label: 'Lỗi ký' },
                  { value: 3, label: 'Chưa gửi CQT' },
                  { value: 4, label: 'Đã cấp mã/tiếp nhận' },
                  { value: 5, label: 'CQT chưa phản hồi' },
                  { value: 6, label: 'Không đủ điều kiện cấp mã' },
                ]}
                size='large'
              />
            </Form.Item> */}
          </div>
        </Form>
      </div>

      <div className='mt-auto border-t border-gray-100 bg-white px-4 py-3'>
        <div className='flex gap-3'>
          <ButtonCommon
            color='dangerSoft'
            size='md'
            radius='rounded'
            className='min-w-[112px] !h-10 !border-gray-200 !text-gray-700'
            onClick={handleClearFilter}
          >
            Cài lại
          </ButtonCommon>
          <ButtonCommon
            color='primary'
            size='md'
            radius='rounded'
            fullWidth
            className='flex items-center justify-center gap-2 !h-10'
            onClick={handleApplyFilter}
          >
            <Funnel size={16} />
            Áp dụng
          </ButtonCommon>
        </div>
      </div>
    </div>
  );
};

export default FilterEInvoice;
